import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router';
import NotFound from './pages/Lost/404.jsx';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from 'store';
import { ProtectedRoute } from 'api/middleware/authMiddleware';
import { authService } from 'api/services/auth';
import Loading from 'pages/Loading/Loading';
import { Navbar, ToastProvider } from 'components';
import { SearchLauncher } from 'components';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useGlobalNotificationListener } from 'hooks/useGlobalNotificationListener';
import useRouteHandler from 'hooks/useRouteHandler.js';
import { ROUTES } from 'utils/constant';
import './App.scss';

const Login = lazy(() => import('./pages/Login/Login'));
const ResetPassword = lazy(
  () => import('./pages/Login/components/ResetPassword')
);
const Admin = lazy(() => import('./pages/Admin/Admin'));
const Build = lazy(() => import('./pages/Build/Build'));
const Prompts = lazy(() => import('./pages/Prompts/Prompts'));
const Contracts = lazy(() => import('./pages/Contracts/Contracts'));
const Landing = lazy(() => import('./pages/Landing/Landing.jsx'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const EmailVerification = lazy(
  () => import('./pages/Login/components/EmailVerification.jsx')
);
const EmailVerificationSent = lazy(
  () => import('./pages/Login/components/EmailVerificationSent.jsx')
);
const LandingDrew = lazy(() => import('./pages/LandingDrew/Landing'));
const LandingJake = lazy(() => import('./pages/LandingJake/Landing'));
const LandingShubham = lazy(() => import('./pages/LandingShubham/Landing'));

// Settings is now a tab within Profile page

function AppRoutes() {
  const dispatch = useDispatch();

  useGlobalNotificationListener();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const { shouldSkipAuthForRoute } = useRouteHandler();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Check if current route should skip authentication (function gets path automatically)
        const routeCheck = shouldSkipAuthForRoute();

        // Skip auth check for routes that don't require authentication
        if (routeCheck.shouldSkipAuth) {
          return;
        }

        await authService.checkAuth(dispatch);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Auth check failed:', error);
        }
      }
    };

    checkAuthentication();
  }, [dispatch]);

  return (
    <>
      {isAuthenticated && <SearchLauncher />}
      <Suspense
        fallback={<div className='flex-justify-center '>Loading...</div>}
      >
        <Routes>
          <Route path={ROUTES.HOME} element={<Landing />} />
          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
          <Route path={ROUTES.LOADING} element={<Loading />} />
          <Route
            path={ROUTES.BUILD_WILDCARD}
            element={
              <ProtectedRoute>
                <Build />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PROMPTS_WILDCARD}
            element={
              <ProtectedRoute>
                <Prompts />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CONTRACTS_WILDCARD}
            element={
              <ProtectedRoute>
                <Contracts />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PROFILE}
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PROFILE_USER}
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN}
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route path={ROUTES.CONFIRM_EMAIL} element={<EmailVerification />} />
          <Route
            path={ROUTES.EMAIL_VERIFICATION_SENT}
            element={<EmailVerificationSent />}
          />
          <Route path={ROUTES.LANDING_DREW} element={<LandingDrew />} />
          <Route path={ROUTES.LANDING_JAKE} element={<LandingJake />} />
          <Route path={ROUTES.LANDING_SHUBHAM} element={<LandingShubham />} />
          <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

// Main App component
function App() {
  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
      <Provider store={store}>
        <ToastProvider>
          <BrowserRouter>
            <div className='App'>
              <div className='app-navbar-wrapper'>
                <Navbar title='GIGACORE' />
              </div>
              <div className='app-content'>
                <AppRoutes />
              </div>
            </div>
          </BrowserRouter>
        </ToastProvider>
      </Provider>
    </GoogleOAuthProvider>
  );
}

export default App;
